import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";

const DISMISSED_KEY = "noir_lead_dismissed";
const SUBMITTED_KEY = "noir_lead_submitted";

export default function LeadCapturePopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });

  const shouldSkip = useCallback(() => {
    return localStorage.getItem(SUBMITTED_KEY) === "1";
  }, []);

  useEffect(() => {
    if (shouldSkip()) return;

    // Initial popup after 3 minutes (180,000 milliseconds)
    const firstTimer = setTimeout(() => setOpen(true), 180000);

    // Recurring popup every 5 minutes (300,000 milliseconds)
    const recurring = setInterval(() => {
      if (shouldSkip()) return;
      setOpen((prev) => (prev ? prev : true));
    }, 300000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(recurring);
    };
  }, [shouldSkip]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISSED_KEY, Date.now().toString());
  };

  const handleFormSubmit = () => {
    // Mark as submitted locally so the popup stops bothering them after they click submit
    localStorage.setItem(SUBMITTED_KEY, "1");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="lead-capture-popup"
    >
      <div className="relative w-full max-w-lg bg-ink/90 backdrop-blur-2xl border border-olive/40 p-8 md:p-10 rise">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-cream/60 hover:text-cream transition-colors"
          aria-label="Close"
          data-testid="lead-popup-close-btn"
        >
          <X size={20} />
        </button>

        <div className="text-[10px] tracking-[0.3em] uppercase text-olive font-semibold mb-4">
          Limited · First-Time Clients
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-semibold text-cream leading-[1.1] mb-3">
          Get <span className="text-olive">20% Off</span> Your First Tattoo
        </h3>
        <p className="text-cream/70 text-sm mb-7 font-body">
          Book a free consultation with one of our resident artists. No pressure, no fluff — just a conversation about your idea.
        </p>

        <form 
          action="https://formspree.io/f/mrpglvvj"
          method="POST" 
          onSubmit={handleFormSubmit}
          className="space-y-3" 
          data-testid="lead-popup-form"
        >
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              name="First Name"
              placeholder="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="bg-transparent border border-olive/30 focus:border-olive outline-none px-4 py-3 text-cream placeholder:text-cream/40 text-sm"
              required
              data-testid="lead-popup-first-name"
            />
            <input
              type="text"
              name="Last Name"
              placeholder="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="bg-transparent border border-olive/30 focus:border-olive outline-none px-4 py-3 text-cream placeholder:text-cream/40 text-sm"
              data-testid="lead-popup-last-name"
            />
          </div>
          <input
            type="tel"
            name="Phone Number"
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-transparent border border-olive/30 focus:border-olive outline-none px-4 py-3 text-cream placeholder:text-cream/40 text-sm"
            required
            data-testid="lead-popup-phone"
          />
          <button
            type="submit"
            className="w-full bg-olive hover:bg-olive-hover text-cream py-3.5 text-xs tracking-[0.28em] uppercase font-medium transition-all"
            data-testid="lead-popup-submit"
          >
            Claim Offer
          </button>
          <p className="text-[10px] text-cream/40 tracking-wide text-center pt-1">
            By submitting, you agree to receive a single SMS follow-up.
          </p>
        </form>
      </div>
    </div>
  );
}
